import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  private history: string[] = [];
  private readonly maxHistoryLength = 20;

  // Routes that should preserve query parameters in history
  private readonly queryParamPreservingRoutes = [
    'dashboard/timeseries/missing',
    'dashboard',
    'meteringpoint',
    'connectionpoint',
  ];

  constructor(private readonly router: Router) {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.addToHistory(event.urlAfterRedirects);
      });
  }

  /**
   * Add URL to history, handling child routes intelligently
   */
  private addToHistory(url: string): void {
    const shouldPreserveParams = this.shouldPreserveQueryParams(url);
    const normalizedUrl = shouldPreserveParams ? url : this.normalizeUrl(url);

    // Don't add if it's the same as the last entry
    if (this.history.length > 0 && this.history.at(-1) === normalizedUrl) {
      return;
    }

    // Check if this is a tab navigation within the same detail page
    if (this.history.length > 0) {
      const lastUrl = this.history.at(-1);
      if (lastUrl && this.isSameDetailPage(lastUrl, normalizedUrl)) {
        // Replace the last entry instead of adding (tab navigation)
        this.history[this.history.length - 1] = normalizedUrl;
        return;
      }
    }

    // Special handling: Replace previous entry if it's the same base route with different params
    if (this.history.length > 0 && shouldPreserveParams) {
      const lastUrl = this.history.at(-1);
      if (lastUrl && this.isSameRouteWithDifferentParams(lastUrl, normalizedUrl)) {
        // Replace the last entry with the new one (updated search params)
        this.history[this.history.length - 1] = normalizedUrl;
        return;
      }
    }

    // Add as new entry
    this.history.push(normalizedUrl);

    // Keep history manageable
    if (this.history.length > this.maxHistoryLength) {
      this.history.shift();
    }
  }

  /**
   * Check if a route should preserve query parameters in history
   */
  private shouldPreserveQueryParams(url: string): boolean {
    const baseUrl = this.normalizeUrl(url);
    return this.queryParamPreservingRoutes.some(route => {
      const normalizedRoute = route.startsWith('/') ? route : `/${route}`;
      return baseUrl === normalizedRoute || baseUrl.startsWith(`${normalizedRoute}/`);
    });
  }

  /**
   * Check if two URLs are the same route with different query parameters
   */
  private isSameRouteWithDifferentParams(url1: string, url2: string): boolean {
    const base1 = this.normalizeUrl(url1);
    const base2 = this.normalizeUrl(url2);

    // Same base route but different full URLs (meaning different params)
    return base1 === base2 && url1 !== url2;
  }

  /**
   * Normalize URL by removing query params and fragments
   */
  private normalizeUrl(url: string): string {
    return url.split('?')[0].split('#')[0];
  }

  /**
   * Check if two URLs are tabs on the same detail page
   * Examples:
   * - /meteringpoint/123 and /meteringpoint/123/billing -> same page (tabs)
   * - /meteringpoint/123 and /meteringpoint/456 -> different pages
   * - /dashboard and /dashboard/specificDashboard -> different pages
   */
  private isSameDetailPage(url1: string, url2: string): boolean {
    const segments1 = url1.split('/').filter(Boolean);
    const segments2 = url2.split('/').filter(Boolean);

    // Define routes that have detail pages with tabs
    const detailRoutes = ['meteringpoint', 'connectionpoint', 'chargeinformation', 'vee', 'settlement'];

    // Check if both URLs are for the same detail route type
    if (segments1.length === 0 || segments2.length === 0) {
      return false;
    }

    const route1 = segments1[0];
    const route2 = segments2[0];

    // Must be the same base route
    if (route1 !== route2) {
      return false;
    }

    // Must be a detail route type
    if (!detailRoutes.includes(route1)) {
      return false;
    }

    // Must have at least 2 segments (route + id)
    if (segments1.length < 2 || segments2.length < 2) {
      return false;
    }

    // Check if the ID (second segment) is the same
    if (segments1[1] !== segments2[1]) {
      return false;
    }

    // If we get here, they're tabs on the same detail page
    // e.g., /meteringpoint/123 and /meteringpoint/123/billing
    return true;
  }

  /**
   * Navigate back to the previous meaningful page
   */
  public back(): Promise<boolean> {
    if (this.history.length > 1) {
      // Remove current URL
      this.history.pop();

      // Get previous URL
      const previousUrl = this.history.at(-1);

      if (previousUrl) {
        return this.router.navigateByUrl(previousUrl);
      }
    }

    // Fallback: navigate to parent route or dashboard
    return this.navigateToParent();
  }

  /**
   * Navigate to the parent route of the current URL
   */
  private navigateToParent(): Promise<boolean> {
    const currentUrl = this.router.url.split('?')[0];
    const segments = currentUrl.split('/').filter(Boolean);

    if (segments.length > 1) {
      // Remove the last segment to get parent
      segments.pop();
      return this.router.navigate(['/' + segments.join('/')]);
    }

    // Fallback to dashboard
    return this.router.navigate(['/dashboard']);
  }

  /**
   * Check if there's a previous URL to navigate to
   */
  public hasBack(): boolean {
    return this.history.length > 1;
  }

  /**
   * Get the previous URL without navigating
   */
  public getPreviousUrl(): string | undefined {
    return this.history.length > 1 ? this.history.at(-2) : undefined;
  }

  /**
   * Clear navigation history
   */
  public clearHistory(): void {
    this.history = [];
  }

  /**
   * Get current navigation history (for debugging)
   */
  public getHistory(): string[] {
    return [...this.history];
  }

  /**
   * Add a route pattern to preserve query params
   * Useful for dynamically adding routes at runtime
   */
  public addQueryParamPreservingRoute(route: string): void {
    if (!this.queryParamPreservingRoutes.includes(route)) {
      this.queryParamPreservingRoutes.push(route);
    }
  }

  /**
   * Remove a route pattern from query param preservation
   */
  public removeQueryParamPreservingRoute(route: string): void {
    const index = this.queryParamPreservingRoutes.indexOf(route);
    if (index > -1) {
      this.queryParamPreservingRoutes.splice(index, 1);
    }
  }
}
